/**
 * @license
 * Copyright (c) 2026 STACK SOFTWARE. All Rights Reserved.
 * Proprietary and confidential. See LICENSE in the project root.
 */
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { ChunkLoadRecoveryService } from './@core/services/chunk-load-recovery.service';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { CoreModule } from './@core/core.module';
import { ThemeModule } from './@theme/theme.module';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import {
  NbButtonModule,
  NbDatepickerModule,
  NbDialogModule,
  NbMenuModule,
  NbSidebarModule,
  NbToastrModule,
  NbUserModule,
  NbWindowModule,
  NB_TIME_PICKER_CONFIG,
} from '@nebular/theme';
import { GraphQLModule } from './graphql.module';
import { EnvironmentModule, EnvService } from '@environment';
import { FormBuilder } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Apollo } from 'apollo-angular';
import { IConfig, NgxMaskModule } from 'ngx-mask';
import { AuthGuard } from './@core/authentication/auth.guard';
import { OverlayModule, OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
// These feature modules declare detail components (WorkOrder/ServiceVisit/Equipment)
// that are opened dynamically in floating windows from anywhere in the app. They are
// imported eagerly here so Angular applies their template (pipe/directive) scope
// app-wide; otherwise opening them before their lazy route has loaded throws NG0302
// ("pipe 'date' could not be found"). Keeping them as NgModule components (rather than
// standalone) avoids the runtime circular-dependency errors (NG0919) that the standalone
// conversion introduced.
import { ServiceVisitDetailModule } from './pages/service-visit/pages/service-visit-detail/service-visit-detail.module';
import { WorkOrderModule } from './pages/workorder/workorder.module';
import { EquipmentModule } from './pages/equipment/equipment.module';
// NOTE: @lottiefiles/lottie-player and @dotlottie/player-component are no longer
// imported eagerly here. They are side-effect imported in the specific lazy
// components that render <lottie-player> / <dotlottie-player>, keeping ~677KB
// of web-component code out of the initial main.js bundle.
export const options: Partial<null | IConfig> | (() => Partial<IConfig>) = null;

import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import { environment } from '../environments/environment';

// Check if running in iframe (for redirect handling)
const isIframe = window !== window.parent && !window.opener;

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  // Determine the correct redirect URI based on current URL
  let redirectUri = environment.azureAd.redirectUri;

  if (typeof window !== 'undefined') {
    if (window.location.href.includes('app.stacksoftware.com')) {
      redirectUri = redirectUri.replace(
        'prod.stacksoftware.com',
        'app.stacksoftware.com',
      );
    }
    if (window.location.href.includes('app.nextfieldservice.com')) {
      redirectUri = redirectUri.replace(
        'dev.nextfieldservice.com',
        'app.nextfieldservice.com',
      );
    }
  }

  return new PublicClientApplication({
    auth: {
      clientId: environment.azureAd.clientId,
      authority: environment.azureAd.authority,
      redirectUri: redirectUri,
      postLogoutRedirectUri: redirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: true, // Set to true for IE11 or Edge
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', [
    'user.read',
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: environment.azureAd.scopes,
    },
  };
}

// Initialize MSAL before app starts
export function initializeMsal(msalService: MsalService): () => Promise<void> {
  return () => msalService.instance.initialize();
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    AppRoutingModule,
    CommonModule,
    MsalModule,
    NbSidebarModule.forRoot(),
    NbMenuModule.forRoot(),
    NbDatepickerModule.forRoot(),
    NbDialogModule.forRoot(),
    NbWindowModule.forRoot(),
    NbToastrModule.forRoot(),
    NbButtonModule,
    CoreModule.forRoot(),
    ThemeModule.forRoot(),
    GraphQLModule,
    NbUserModule,
    NgxMaskModule.forRoot(),
    EnvironmentModule,
    OverlayModule,
    ServiceVisitDetailModule,
    WorkOrderModule,
    EquipmentModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent, MsalRedirectComponent],
  providers: [
    {
      // Angular CDK v20 renders overlays (Nebular windows/dialogs/popovers) in
      // the browser's native top layer via the Popover API (`popover="manual"`).
      // Top-layer elements always paint above normal DOM regardless of z-index,
      // which broke the PhotoSwipe image viewer (it appeared behind windows after
      // the Angular upgrade). Disabling `usePopover` restores the previous
      // z-index-based overlay stacking so the viewer can sit above windows again.
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    {
      provide: NB_TIME_PICKER_CONFIG,
      useValue: {},
    },
    FormBuilder,
    Apollo,
    AuthGuard,
    EnvService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MsalService],
      multi: true,
    },
    // Must be listening before the first lazy navigation, so a tab left open
    // across a deploy reloads instead of dead-clicking. See the service.
    provideAppInitializer(() => inject(ChunkLoadRecoveryService).start()),
    MsalService,
    MsalGuard,
    MsalBroadcastService,
  ],
})
export class AppModule {}
